
import { Component, Watch, Mixins, Prop } from 'vue-property-decorator';
import { makeFormModel, toDataModel, updateModel } from 'ah-common-lib/src/form/helpers';
import { radioField, textField, emailField, phoneField } from 'ah-common-lib/src/form/models';
import TermsAndConditionsLink from '@/app/components/common/TermsAndConditionsLink.vue';
import {
  ClientFileCategories,
  ClientFileCategoriesToHuman,
  ClientFileCategoryDescriptions,
  UnsubmittedUbo,
  UploadedFile,
  VersionedObject,
} from 'ah-api-gateways';
import WithRequestManager from 'ah-common-lib/src/requestManager/WithRequestManager.vue';
import { generateUUID } from 'ah-common-lib/src/helpers/uuid';
import { FormModel, FormEvent, FormValidation } from 'ah-common-lib/src/form/interfaces';
import { cloneDeep, isEqual } from 'lodash';
import { requiredIfStateValue, optional, checkParam, validName } from 'ah-common-lib/src/form/validators';
import FileUploader from 'ah-common-lib/src/common/components/upload/FileUploader.vue';
import { personTitleField } from 'ah-common-lib/src/form/formModels';
import { defaultAcceptanceFileTypes } from 'ah-common-lib/src/helpers/file';
import { map, tap } from 'rxjs/operators';
import { useAuthStore } from '@/app/store/authModule';
import { useIndividualSettingsStore } from '@/app/store/individualSettingsModule';
import { waitForEntityChange } from 'ah-requests';

const uboUserFM = () =>
  makeFormModel({
    name: 'uboUserForm',
    fieldType: 'form',
    fields: [
      personTitleField({ fieldWrapperClass: 'col col-2', showRequiredMarkers: true }),
      textField(
        'firstName',
        'Name',
        { fieldWrapperClass: 'col col-5', showRequiredMarkers: true },
        { required: requiredIfStateValue('firstName'), validName }
      ),
      textField(
        'lastName',
        'Surname',
        { fieldWrapperClass: 'col col-5', showRequiredMarkers: true },
        { required: requiredIfStateValue('firstName'), validName }
      ),
      emailField('email', 'Signatory email address', { showRequiredMarkers: true }),
      phoneField(
        'phoneNumber',
        'Mobile Number',
        { class: 'col col-md-7' },
        {
          phone: optional(checkParam('phoneNumber', 'valid')),
        }
      ),
    ],
  });

const uboChoiceFM = () =>
  makeFormModel({
    name: 'uboChoiceForm',
    fieldType: 'form',
    fields: [
      radioField(
        'choice',
        'Are there any UBOs?',
        [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        {
          defaultValue: 'no',
          inline: true,
          fieldWrapperClass: 'mb-0 col col-12',
          showRequiredMarkers: true,
        }
      ),
    ],
  });

const MAX_ALLOWED_UBOS = 4;

/**
 * Ultimate beneficiary owner(s) form
 *
 * The current component will display a form to add (if any) `maxUBO` number
 * of UBO users.
 *
 * Emits:
 * - update:ubos (payload: UnsubmittedUbo[])
 * - update:files (payload: UploadedFile[])
 */
@Component({
  components: { TermsAndConditionsLink, FileUploader },
})
export default class UBOForm extends Mixins(WithRequestManager) {
  @Prop({ default: () => [] }) ubos!: UnsubmittedUbo[];

  /**
   * Files presented to client
   */
  @Prop({ default: () => [] }) files!: UploadedFile[];

  private ClientFileCategories = ClientFileCategories;

  private uboUsers: UnsubmittedUbo[] = [];

  private uboChoiceForm = uboChoiceFM();

  private uboChoiceValidation: FormValidation | null = null;

  private uboUserForms: FormModel[] = [];

  private uboUserValidations: (FormValidation | null)[] = [];

  private defaultAcceptanceFileTypes = defaultAcceptanceFileTypes;

  private innerFiles: UploadedFile[] = [];

  private resolutionLetterTitle = ClientFileCategoriesToHuman[ClientFileCategories.RESOLUTION_LETTER];

  private resolutionLetterDescription = ClientFileCategoryDescriptions[ClientFileCategories.RESOLUTION_LETTER];

  private generateTempId() {
    return generateUUID();
  }

  get authStore() {
    return useAuthStore();
  }

  get individualSettingsStore() {
    return useIndividualSettingsStore();
  }

  get resolutionLetter() {
    return this.innerFiles.find((document) => document.category === ClientFileCategories.RESOLUTION_LETTER) || null;
  }

  get shouldDisplayUbos() {
    return this.uboChoiceForm.choice === 'yes';
  }

  get validUploadedFiles() {
    return true;

    // FIXME: documents are no longer required in UBOs (temporary fix)

    // if (!this.shouldDisplayUbos) {
    //   return true;
    // } else {
    //   const anyIncompleteUboFiles = this.uboUsers.find((user) =>
    //     [ClientFileCategories.PROOF_OF_ADDRESS, ClientFileCategories.PHOTO_ID].find(
    //       (cat) =>
    //         !user.documents.find((d) => d.category === cat) &&
    //         !user.readyToUpload?.find((d) => d.category === cat && d.file)
    //     )
    //   );

    //   return !anyIncompleteUboFiles;
    // }
  }

  get validation() {
    return {
      $model: null,
      $invalid:
        this.uboChoiceValidation?.$invalid ||
        !this.validUploadedFiles ||
        !!this.uboUserValidations?.find((val) => val?.$invalid),
      $dirty: !!this.uboChoiceValidation?.$dirty || !!this.uboUserValidations?.find((val) => val?.$dirty),
    };
  }

  get emptyUser(): UnsubmittedUbo {
    return {
      firstName: '',
      lastName: '',
      phoneNumber: '',
      email: '',
      documents: [],
      readyToUpload: [
        { category: ClientFileCategories.PROOF_OF_ADDRESS, file: null },
        { category: ClientFileCategories.PHOTO_ID, file: null },
      ],
    };
  }

  get maxAllowedUsersReached() {
    return this.uboUsers.length >= MAX_ALLOWED_UBOS;
  }

  private uploadResolutionLetter(file: File) {
    return this.$services.client
      .uploadDocument(this.authStore.loggedInIdentity!.client!.id, ClientFileCategories.RESOLUTION_LETTER, file)
      .pipe(
        tap((update) => {
          if (update.finished) {
            this.waitForDocument(update.file).subscribe((document) => {
              if (document) {
                this.individualSettingsStore.setClientDocument({ document });
                const currFileIndex = this.innerFiles.findIndex((i) => i.category === document.category);

                currFileIndex > -1
                  ? this.innerFiles.splice(currFileIndex, 1, document)
                  : this.innerFiles.push(document);
              }
            });
          }
        })
      );
  }

  private waitForDocument(file: VersionedObject) {
    return waitForEntityChange(
      () => this.$services.client.getDocuments(this.authStore.loggedInIdentity!.client!.id),
      (docs) => {
        return !!docs.find((d) => d.id === file.id);
      }
    ).pipe(map((docs) => docs.find((d) => d.id === file.id)!));
  }

  private deleteFile(file: UploadedFile) {
    return this.$services.client.deleteDocument(this.authStore.loggedInIdentity!.client!.id, file.id).pipe(
      tap(() => {
        const currFileIndex = this.innerFiles.findIndex((i) => i.category === file.category);
        if (currFileIndex > -1) {
          this.innerFiles.splice(currFileIndex, 1);
        }

        this.individualSettingsStore.unsetClientDocument({ category: file.category as ClientFileCategories });
      })
    );
  }

  private downloadFile(file: UploadedFile) {
    return this.$services.client.downloadSyncDocument(this.authStore.loggedInIdentity!.client!.id, file);
  }

  private getFile(user: UnsubmittedUbo, category: ClientFileCategories) {
    return (
      user.readyToUpload?.find((file) => file.category === category)?.file ||
      user.documents?.find((file) => file.category === category)
    );
  }

  private onFileChange(file: File, user: UnsubmittedUbo, category: ClientFileCategories) {
    if (file === null) {
      this.onFileremoved(file, user, category);
    }
    this.$set(user, 'readyToUpload', user.readyToUpload || []);
    let userFile = user.readyToUpload!.find((file) => file.category === category)!;
    if (!userFile) {
      userFile = { category, file: file };
      user.readyToUpload!.push(userFile);
    } else {
      userFile.file = file;
    }
    this.$emit('update:ubos', this.uboUsers);
  }

  private onFileremoved(file: File, user: UnsubmittedUbo, category: ClientFileCategories) {
    user.readyToUpload = user.readyToUpload || [];

    let uploaded = user.documents!.find((file) => file.category === category)!;
    let userFile = user.readyToUpload!.find((file) => file.category === category)!;
    if (uploaded) {
      user.documents.splice(user.documents.indexOf(uploaded), 1);
    }

    if (!userFile) {
      userFile = { category, file: null };
      user.readyToUpload!.push(userFile);
    } else {
      userFile.file = null;
    }

    this.$emit('update:ubos', this.uboUsers);
  }

  private onFormEvent(event: FormEvent) {
    if (event.event === 'form-field-set-value') {
      this.uboUsers = this.uboUsers.map((user, index) => ({
        ...toDataModel(this.uboUserForms[index]),
        documents: user.documents,
        readyToUpload: user.readyToUpload,
      }));
      this.$emit('update:ubos', this.uboUsers);
    }
  }

  private onChoiceChange(event: FormEvent) {
    if (event.event === 'form-field-set-value' && this.shouldDisplayUbos && (!this.ubos || this.ubos.length === 0)) {
      this.addUser();
    } else if (event.event === 'form-field-set-value' && !this.shouldDisplayUbos) {
      this.uboUsers = [];
      this.uboUserForms = [];
      this.uboUserValidations = [];
      this.$emit('update:ubos', this.uboUsers);
    }
  }

  private deleteUbo(uboId?: string) {
    if (uboId) {
      this.requestManager
        .sameOrCancelAndNew(
          `deleteUbo-${uboId}`,
          this.$services.compliance.removeUboUser(this.authStore.loggedInIdentity!.client!.id, uboId)
        )
        .subscribe();
    }
  }

  private remove(index: number) {
    this.deleteUbo(this.uboUsers[index].id);
    if (this.uboUsers.length > 1) {
      this.uboUsers.splice(index, 1);
      this.uboUserValidations[index] = null;
      this.$emit('update:ubos', this.uboUsers);
    } else {
      this.uboUsers = [];
      this.uboUserForms = [];
      this.uboUserValidations = [];
      this.$emit('update:ubos', null);
      this.uboChoiceForm.choice = 'no';
    }
  }

  private addUser(ubo?: UnsubmittedUbo) {
    this.uboUserForms.push(uboUserFM());
    this.uboUsers.push(cloneDeep(ubo ?? this.emptyUser));
    if (!ubo) {
      this.$emit('update:ubos', this.uboUsers);
    } else {
      updateModel(this.uboUserForms[this.uboUserForms.length - 1], ubo);
    }
  }

  @Watch('files', { immediate: true })
  onUboFilesChange() {
    if (this.files !== null) {
      this.innerFiles = this.files;
    }
  }

  @Watch('ubos', { immediate: true })
  onUbosChange() {
    this.uboUserForms = [];
    this.uboUsers = [];
    this.ubos.forEach((ubo) => this.addUser(ubo));
    this.uboChoiceForm.choice = this.uboUsers.length > 0 ? 'yes' : 'no';
  }

  @Watch('innerFiles')
  onFilesChange() {
    if (!isEqual(this.innerFiles, this.files)) {
      this.$emit('update:files', this.innerFiles);
    }
  }

  @Watch('validation', { immediate: true })
  onValidationChange() {
    this.$emit('update:validation', this.validation);
  }
}
